import { useState, useEffect, useContext } from "react";
import { useHref, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useCallback } from "react";
import { OrgIdContext, ClientTypes, CustomerRelations, Domain } from "../store/orgId_context";
import { useQuery } from "@tanstack/react-query";

const apiRequestAddress = process.env.REACT_APP_API_ENDPOINT || window.location.origin + "/json.rpc";

const defaultBlockedData = {
  isBlocked: false,
  info: null,
  redirection: null,
  duration: null,
}; 
/* const defaultBlockedData = {
  isBlocked: true,
  info: null,
  redirection: null,
  duration: null,
}; */

const useSessionInfo = (onSessionLoadedHandler, triggerRerender) => {
  const [clientData, setClientData] = useState({
    name: "Name Surname",
    company: "Company Name",
  });
  const [hasSession, setHasSession] = useState(null);
  const [blockedData, setBlockedData] = useState(defaultBlockedData);
  const [sessionData, setSessionData] = useState(null);

  const { onIsMaintainerHandler,
      setMainOrgHandler,
      setIsTrusteeHandler,
      setIsSuperUserHandler,
      setIsTransformerHandler,
      setIsIntegratorHandler,
      setIsBuilderHandler,
      setClientTypeHandler,
      setISVCustomerHandler,
      setAccCustomerHandler,
      setCustomerRelationHandler,
      setIntegrationNotFinishedHandler,
      setAboutOrgIdHandler,
      setMainCountryHandler,
      setIntegrationDeviceHandler,
      setConnKeyHandler,
      setConnectionKeyModeHandler,
      setSessionInfoHandler,
      setDefaultDeviceHandler,
      setDefaultDeviceModeHandler,
      setDomainHandler,
      setIsLoaded,
      setModeREIHandler,
      setIsSessionChecked,
       } = useContext(OrgIdContext);
  const [searchParams] = useSearchParams();
  
  

  const onCloseBlock = useCallback(() => {
    if (blockedData.redirection) window.location = blockedData.redirection;
    setBlockedData((prev) => ({ ...prev, isBlocked: false }));
  }, [blockedData.redirection]);

  const handleBlocked = useCallback((isBlocked, blockedInfo) => {
    let desc,
      redirection,
      duration = null;
    if (blockedInfo) {
      desc = blockedInfo.Desc || null;
      redirection = blockedInfo.RedirectLink || null;
      duration = blockedInfo.DelayDuration || null;
    }

    setBlockedData({
      isBlocked,
      desc,
      redirection,
      duration,
    });

    if (isBlocked && duration !== undefined && duration >= 1) {
      setTimeout(() => {
        if (redirection) {
          window.location = redirection || "@";
        } else {
          setBlockedData((prev) => ({
            ...prev,
            isBlocked: false,
          }));
        }
      }, duration * 1000);
    }
  }, []);

  const handleSessionLoad = useCallback(
    (result) => {
      console.log("Session Info loaded:", result);
      //result.Extended.Theme = "duck";
      setSessionInfoHandler(result);
      setSessionData(result);
      const extended = result.Extended || {};
      
      setClientData({
        name: result.User.Name,
        company: result.Organization?.Title,
      });
      setMainOrgHandler(result.Organization?.Id, result.User.Name);
      setMainCountryHandler(result.Extended?.CountryCodeSC__CountryCode);
      if (!sessionStorage.getItem("mainOrgId") && result.Organization?.Id) {
        sessionStorage.setItem("mainOrgId", result.Organization.Id);
      }
      result?.BaseUrl?.indexOf("dddinvoices")>-1 ? setDomainHandler(Domain.DDDInvoices) : setDomainHandler(Domain.EInvoices);


      if(extended.AboutOrgId)
        setAboutOrgIdHandler(extended.AboutOrgId);
      if(extended.IntegrationDevice)
        setIntegrationDeviceHandler(extended.IntegrationDevice);
      //Note: Device data is not connection key. Though sometimes they can be the same Id
      if(extended.ConnectionKey)
        setConnKeyHandler(extended.ConnectionKey)
      if(extended.ModeIEI)
        setConnectionKeyModeHandler(extended.ModeIEI);
      if(extended.ModeREI)
        setModeREIHandler(extended.ModeREI);
      if(extended.DeviceData?.Id)
        setDefaultDeviceHandler(extended.DeviceData?.Id);
      if(extended.DeviceData?.Mode)
        setDefaultDeviceModeHandler(extended.DeviceData?.Mode);

      const isTransformer = extended.IsTransformer;

      //TODO: IsTransformer is getting removed from extended session and getting replaced with ClientType=ISV
      if (isTransformer){
        //setIsTransformerHandler(true);
      }
      if(isTransformer || extended.ClientType === ClientTypes.ISV)
        handleBlocked(extended.Blocked, extended.BlockedInfo)
      
      if(extended.IsIntegrator)
        setIsIntegratorHandler(true);
      if(extended.IsBuilder)
        setIsBuilderHandler(true);
      if(extended.ClientType)
        setClientTypeHandler(extended.ClientType);
      if(extended.ISVCustomer)
        setISVCustomerHandler(extended.ISVCustomer);
      if(extended.AccSCustomer)
        setAccCustomerHandler(extended.AccSCustomer);
      if(extended.CustomerRelation)
        setCustomerRelationHandler(extended.CustomerRelation);

      if(extended.IntegrationNotFinished)
        setIntegrationNotFinishedHandler(true);

      onIsMaintainerHandler(result.Roles.includes("maintainer"));
      setIsTrusteeHandler(result.Roles.includes("trustee"));
      setIsSuperUserHandler(result.Roles.includes("superuser"));
      //TODO: Remove this when extended session returns the correct data
      fakeAppBehaviourViaURL();

      onSessionLoadedHandler(true, isTransformer, result);
      setHasSession(true);
      setIsLoaded(true);
      setIsSessionChecked(true);
    },
    [
      handleBlocked,
      onIsMaintainerHandler,
      onSessionLoadedHandler,
      setMainCountryHandler,
      setMainOrgHandler,
    ]
  );
  
  const pathNames = useHref("/")?.replace(/^\/|\/$/g, '').split("/") || [];
  const fakeAppBehaviourViaURL = () => {
    if(searchParams.has("IsTransformer"))
      setIsTransformerHandler(searchParams.get("IsTransformer")==="false" ? false : true);
    if(searchParams.has("IsIntegrator"))
      setIsIntegratorHandler(searchParams.get("IsIntegrator")==="false" ? false : true);
    if(searchParams.has("IsBuilder"))
      setIsBuilderHandler(searchParams.get("IsBuilder")==="false" ? false : true);

    if(searchParams.has("ClientType")){
      if(ClientTypes.hasOwnProperty(searchParams.get("ClientType")))
        setClientTypeHandler(ClientTypes[searchParams.get("ClientType")]);
    }
    if(searchParams.has("CustomerRelation")){
      if(CustomerRelations.hasOwnProperty(searchParams.get("CustomerRelation")))
      setCustomerRelationHandler(CustomerRelations[searchParams.get("CustomerRelation")]);
    }
      
    if(searchParams.has("ISVCustomer"))
      setISVCustomerHandler(searchParams.get("ISVCustomer")==="false" ? false : true);
    if(searchParams.has("AccSCustomer"))
      setAccCustomerHandler(searchParams.get("AccSCustomer")==="false" ? false : true);
    /*if(searchParams.has("CustomerRelation"))
      setCustomerRelationHandler(searchParams.get("CustomerRelation"));*/
    if(searchParams.has("Trustee"))
      setIsTrusteeHandler(searchParams.get("Trustee")==="false" ? false : true);
    if(searchParams.has("Domain")){
      if(Domain.hasOwnProperty(searchParams.get("Domain")))
        setDomainHandler(Domain[searchParams.get("Domain")])
    } 
  }

  useEffect(() => {
    axios
      .post(
        apiRequestAddress,
        {
          jsonrpc: "2.0",
          //method: "Auth.GetSessionInfo",
          method: "Auth.Describe",
          params: {
            includeExtendedInfo: true,
          },
          id: 12,
        },
        {
          withCredentials: true,
        }
      )
      .then((res) => {
        if (res?.data?.result?.HasSession) handleSessionLoad(res.data.result);
        else { onSessionLoadedHandler(false, false, res?.data?.result); setIsSessionChecked(true); }
      });
  }, [handleSessionLoad, onSessionLoadedHandler, triggerRerender]);

  return { blockedData, clientData, hasSession, onCloseBlock, sessionData };
};

export default useSessionInfo;
